import { AvaStore } from '@models';

interface Cluster {
  clusterID: string;
  clusterName: string;
  stores?: {
    id: string;
    name: string;
  }[];
}

interface Store {
  id: string;
  name: string;
}

const generateNewClusterID = (storesByCluster: Cluster[], searchState: string): string => {
  if (storesByCluster.length) {
    const vicClusterIDs = storesByCluster
      .map((cluster) => cluster.clusterID)
      .filter((clusterID) => clusterID.startsWith(`${searchState}_`));

    const numericSuffixes = vicClusterIDs
      .map((clusterID) => parseInt(clusterID.replace(`${searchState}_`, '')))
      .filter((suffix) => !isNaN(suffix));
    const maxSuffix = Math.max(...numericSuffixes);

    return `${searchState}_${maxSuffix + 1}`;
  } else {
    return `${searchState}_1`;
  }
};

const formatStoreClusters = (deletedStores: AvaStore[], newStores: AvaStore[], clusterID: string, clusterName: string): AvaStore[] => {
  const formattedStores: AvaStore[] = [];

  deletedStores.forEach((store) => {
    const clustersWithoutID = store.cluster?.filter((cluster) => cluster.id !== clusterID);
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { _id, ...formattedStore } = store;

    formattedStore.cluster = clustersWithoutID;
    formattedStores.push(formattedStore);
  });

  newStores.forEach((store) => {
    const clusters = store.cluster || [];
    const newCluster = { id: clusterID, name: clusterName };
    const updatedCluster = [...clusters, newCluster];

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { _id, ...rest } = store;
    const formattedStore: AvaStore = { ...rest, cluster: updatedCluster };

    formattedStores.push(formattedStore);
  });

  return formattedStores;
};

const findDeletedAndNewStores = (
  cluster: Cluster,
  stores: Store[],
  storesInState: AvaStore[],
): { deletedStores: AvaStore[]; newStores: AvaStore[] } => {
  if (!cluster.stores) {
    const newStores = storesInState.filter((store) => stores.some((newStore) => newStore.id === store.store_code));

    return { deletedStores: [], newStores: newStores };
  }

  const clusterStoreIds = cluster.stores.map((store) => store.id);
  const newStoreIds = stores.map((store) => store.id);

  const deletedStoreIds = cluster.stores.filter((store) => !newStoreIds.includes(store.id));
  const newlyAddedStoreIds = stores.filter((store) => !clusterStoreIds.includes(store.id));

  const deletedStores = storesInState.filter((store) => deletedStoreIds.some((deletedStore) => deletedStore.id === store.store_code));
  const newStores = storesInState.filter((store) => newlyAddedStoreIds.some((newStore) => newStore.id === store.store_code));

  return { deletedStores, newStores };
};

export { generateNewClusterID, formatStoreClusters, findDeletedAndNewStores };
